import { cn } from "@dl/web-core-js/ui";
import { WEEKS_TO_OOS_UNBOUNDED } from "../api/pipeline";

/**
 * Weeks of stock drawn against the horizon being planned to.
 *
 * Shared by the transfer and pipeline screens because both ask the same
 * question, only against different horizons: how much cover is there, measured
 * against how much we wanted.
 *
 * Both ends of the scale draw an empty bar, because neither has cover to show:
 * one has run out, the other has no demand to run out of. The out of stock case
 * is ringed in the risk colour so the two are not mistaken for each other.
 *
 * `reorder` draws the reorder point as a tick on the scale: cover to the left
 * of it is inside the point and fills in the warning tone, cover past it is
 * fine. That is the whole policy in one glance — where the run starts, where
 * it lands — without a number in sight.
 */
export function CoverBar({ weeks, target, reorder }: { weeks: number; target: number; reorder?: number }) {
  const unbounded = weeks >= WEEKS_TO_OOS_UNBOUNDED;
  const out = weeks <= 0;
  const scale = Math.max(target, 1);
  const fill = unbounded || out ? 0 : Math.min(weeks / scale, 1);
  const inside = reorder !== undefined ? weeks <= reorder : weeks < target;
  const tone = inside ? "bg-warn" : "bg-ok";
  const tick = reorder !== undefined && reorder > 0 && reorder < target ? reorder / scale : null;

  return (
    <span
      className={cn(
        "relative h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-surface-sunk",
        out && "border border-risk",
      )}
    >
      <span
        className={cn("block h-full rounded-full", tone)}
        style={{ width: `${Math.round(fill * 100)}%` }}
      />
      {tick !== null && (
        <span
          aria-hidden
          className="absolute top-0 h-full w-px bg-ink-faint"
          style={{ left: `${Math.round(tick * 100)}%` }}
        />
      )}
    </span>
  );
}
